import { Menu } from '@base-ui/react/menu'
import { twMerge } from 'tailwind-merge'
import type { PartProps } from '../../../lib/part-props'

/**
 * Props interface for MenuGroup component.
 * Extends base-ui's Menu.Group.Props for group container functionality.
 */
interface MenuGroupProps extends PartProps<Menu.Group.Props> {
  className?: string
}

/**
 * MenuGroup - Groups related menu items with an associated label.
 *
 * This component provides a semantic grouping of menu items, typically
 * used with a MenuGroupLabel to provide a heading for the group.
 *
 * Groups help organize complex menus by visually and semantically
 * separating related items.
 *
 * Features:
 * - Proper ARIA grouping for accessibility
 * - Automatic label association via MenuGroupLabel
 * - Flexible styling options
 *
 * @example
 * ```tsx
 * <Menu.Group>
 *   <Menu.GroupLabel>Sort by</Menu.GroupLabel>
 *   <Menu.RadioGroup value={sort} onValueChange={setSort}>
 *     <Menu.RadioItem value="date">Date</Menu.RadioItem>
 *     <Menu.RadioItem value="name">Name</Menu.RadioItem>
 *   </Menu.RadioGroup>
 * </Menu.Group>
 * ```
 */
function MenuGroup({ className, children, ...props }: MenuGroupProps) {
  return (
    <Menu.Group className={twMerge(className)} {...props}>
      {children}
    </Menu.Group>
  )
}

export { MenuGroup }
